import { RatesRowData } from '../../applications/rates/pages/ratesDataGrid/RatesDataGrid'
import { EntityType } from '../../api/models/apiTypes.model'
import { ZoneAuxList } from '../../api/models/zoneAuxList.model'
import {
  AvailabilityDataList,
  ChannelDataList,
  DayDataList,
  DayDataTypes,
  HotelDataList,
  RoomTypeCapacityDataList,
  RoomTypeDataList,
  RoomTypeRateDataList
} from '../../api/models/dataList.model'
import { EnvironmentDataAuxList } from '../../api/models/environmentDataAuxList.model'
import { HotelAuxList } from '../../api/models/hotelAuxList.model'
import { RoomTypeAuxList } from '../../api/models/roomTypeAuxList.model'
import { TreeItemI } from '../../components/customTree/CustomTree'
import { v4 } from 'uuid'

const availabilityDataToRowData = (dayDataList: AvailabilityDataList[]) =>
  dayDataList.reduce<{ [key: string]: any }>((acc, dayData) => {
    acc[dayData.date] = dayData ?? ''
    return acc
  }, {})

const dayDataToRowData = (
  dayDataList: DayDataList[],
  dataType: keyof DayDataTypes
) =>
  dayDataList.reduce<{ [key: string]: any }>((acc, dayData) => {
    acc[dayData.date] = dayData[dataType] ?? ''
    return acc
  }, {})

const channelToRowData = ({
  channel,
  fatherId,
  hotelId,
  rateId,
  roomTypeId,
  defaultShow,
  oldRows,
  roomTypeCapacityId
}: {
  channel: ChannelDataList
  fatherId: string
  hotelId: string
  roomTypeId: string
  rateId: string
  oldRows?: RatesRowData[]
  defaultShow?: boolean
  roomTypeCapacityId: string
}): RatesRowData => {
  const fatherRow = oldRows?.find((row) => row.id === fatherId)
  const channelId = `${fatherId}-${channel.id}`
  const oldChannel = oldRows?.find((oldRow) => oldRow.id === channelId)
  const channelRowData = {
    col1: channel.name,
    dayData: channel.dayDataList,
    ...dayDataToRowData(channel.dayDataList, fatherRow?.dataType ?? 'price')
  }
  return {
    id: channelId,
    type: 'channel',
    fatherId,
    inherited: channel.inherited,
    editable: channel.editable,
    dataType: fatherRow?.dataType ?? 'price',
    show: defaultShow || oldChannel?.show || false,
    inheritedDataList: channel.inheritedDataList,
    ...channelRowData,
    hotelId,
    roomTypeId,
    rateId,
    roomTypeCapacityId
  }
}

const channelsToRowData = ({
  channels,
  fatherId,
  hotelId,
  roomTypeId,
  oldRows,
  defaultShow,
  rateId,
  roomTypeCapacityId
}: {
  channels: ChannelDataList[]
  fatherId: string
  hotelId: string
  roomTypeId: string
  oldRows?: RatesRowData[]
  defaultShow?: boolean
  rateId: string
  roomTypeCapacityId: string
}) => {
  return channels.map((channel) =>
    channelToRowData({
      channel,
      fatherId,
      hotelId,
      roomTypeId,
      oldRows,
      defaultShow,
      rateId,
      roomTypeCapacityId
    })
  )
}

const capacityDataToRatesRowData = ({
  capacityData,
  roomTypeData,
  rate,
  oldRows,
  hotelData,
  fatherId,
  defaultShow,
  childrenRateId
}: {
  fatherId: string
  childrenRateId: string
  capacityData: RoomTypeCapacityDataList
  roomTypeData: RoomTypeDataList
  rate: RoomTypeRateDataList
  oldRows?: RatesRowData[]
  hotelData: HotelDataList
  defaultShow?: boolean
}): RatesRowData => {
  const fatherRow = oldRows?.find((row) => row.id === fatherId)
  const childrenRatefatherChannel = capacityData.channelDataList[0]
  const oldChildrenRate = oldRows?.find(
    (oldRow) => oldRow.id === childrenRateId
  )
  const childrenRateRowData = {
    col1: `${rate.name} (${capacityData.name} pax)`,
    ...dayDataToRowData(
      childrenRatefatherChannel.dayDataList,
      fatherRow?.dataType ?? 'price'
    )
  }
  return {
    id: childrenRateId,
    type: 'rate',
    fatherId,
    dataType: fatherRow?.dataType ?? 'price',
    show: defaultShow ?? oldChildrenRate?.show ?? false,
    ...childrenRateRowData,
    dayData: childrenRatefatherChannel.dayDataList,
    hotelId: hotelData.id,
    roomTypeId: roomTypeData.id,
    rateId: rate.id,
    roomTypeCapacityId: capacityData.id,
    editable: childrenRatefatherChannel.editable,
    inherited: childrenRatefatherChannel.inherited,
    numberChannelList: +capacityData.channelNumber,
    numberCapacityList: rate.numberCapacityList,
    roomTypeCapacityList: rate.roomTypeCapacityList
  }
}

const capacityDataListToRatesRowData = ({
  capacityList,
  fatherId,
  hotelData,
  rate,
  oldRows,
  roomTypeData,
  defaultShow
}: {
  capacityList: RoomTypeCapacityDataList[]
  fatherId: string
  hotelData: HotelDataList
  rate: RoomTypeRateDataList
  oldRows?: RatesRowData[]
  roomTypeData: RoomTypeDataList
  defaultShow?: boolean
}): RatesRowData[] => {
  return capacityList.reduce((acc, capacityData) => {
    const childrenRateId = `${roomTypeData.id}-${rate.id}-${capacityData.id}-${capacityData.channelDataList[0].id}`
    acc = [
      ...acc,
      {
        ...capacityDataToRatesRowData({
          capacityData,
          fatherId,
          hotelData,
          rate,
          oldRows,
          roomTypeData,
          defaultShow,
          childrenRateId
        })
      }
    ]
    for (let index = 0; index < capacityData.channelNumber; index++) {
      acc = [
        ...acc,
        {
          type: 'channelSkeleton',
          id: v4(),
          fatherId: childrenRateId
        }
      ]
    }
    return acc
  }, [] as RatesRowData[])
}

const hotelDataListToRatesRowData = (
  hotelDataList: HotelDataList[],
  oldRows?: RatesRowData[]
) => {
  return (
    hotelDataList.reduce<RatesRowData[]>((acc, hotelData) => {
      const hotelRowData = {
        col1: hotelData.name
      }
      acc = [
        ...acc,
        {
          id: hotelData.id,
          type: 'hotel',
          show: true,
          ...hotelRowData
        }
      ]

      // roomTypes
      hotelData.roomTypeDataList.map((roomTypeData) => {
        const roomTypeRowData = {
          col1: `${roomTypeData.name} (${roomTypeData.totalRooms})`,
          ...availabilityDataToRowData(roomTypeData.availabilityDataList)
        }
        acc = [
          ...acc,
          {
            id: roomTypeData.id,
            type: 'roomType',
            show: true,
            ...roomTypeRowData
          }
        ]
        // father rates
        return roomTypeData.rateDataList.map((rateData) => {
          const capacityListQty = rateData.numberCapacityList - 1
          const fatherCapacityData = rateData.roomTypeCapacityDataList[0]
          const fatherChannel = fatherCapacityData.channelDataList[0]
          const haveChildren = capacityListQty >= 1
          const rateDataId = `${roomTypeData.id}-${rateData.id}-${fatherCapacityData.id}-${fatherChannel.id}`
          const oldFatherRateRowData = oldRows?.find(
            (oldRow) => oldRow.id === rateDataId
          )
          const fatherRateRowData = {
            col1: `${rateData.name} (${fatherCapacityData.name} pax)`,
            numberChannelList: +fatherCapacityData.channelNumber,
            numberCapacityList: rateData.numberCapacityList,
            ...dayDataToRowData(
              fatherChannel.dayDataList,
              oldFatherRateRowData?.dataType ?? 'price'
            )
          }
          acc = [
            ...acc,
            {
              id: rateDataId,
              type: 'rate',
              haveChildren,
              dataType: oldFatherRateRowData?.dataType ?? 'price',
              show: true,
              ...fatherRateRowData,
              dayData: fatherChannel.dayDataList,
              hotelId: hotelData.id,
              roomTypeId: roomTypeData.id,
              rateId: rateData.id,
              roomTypeCapacityId: fatherCapacityData.id,
              editable: fatherChannel.editable,
              inherited: fatherChannel.inherited,
              roomTypeCapacityList: rateData.roomTypeCapacityList,
              inheritedDataList: fatherChannel.inheritedDataList
            }
          ]

          // add skeleton channels
          for (
            let index = 0;
            index < +fatherCapacityData.channelNumber;
            index++
          ) {
            acc = [
              ...acc,
              {
                type: 'channelSkeleton',
                id: v4(),
                fatherId: rateDataId
              }
            ]
          }

          // childen rates skeletons
          if (haveChildren) {
            for (let index = 0; index < capacityListQty; index++) {
              acc = [
                ...acc,
                {
                  type: 'rateSkeleton',
                  id: v4(),
                  fatherId: rateDataId
                }
              ]
            }
          }

          return null
        })
      })
      return acc
    }, []) ?? []
  )
}

const getRowDataIds = (row: RatesRowData | string) => {
  const ids: {
    roomTypeId: string
    rateId: string
    roomTypeCapacityId: string
    channelId: string
  } = {
    roomTypeId: '',
    rateId: '',
    roomTypeCapacityId: '',
    channelId: ''
  }
  let rowIdSplitted: string[]
  if (typeof row === 'string') {
    rowIdSplitted = row.split('-')
  } else {
    rowIdSplitted = row.id.split('-')
  }
  ids.roomTypeId = rowIdSplitted[0]
  ids.rateId = rowIdSplitted[1]
  ids.roomTypeCapacityId = rowIdSplitted[2]
  ids.channelId = rowIdSplitted[rowIdSplitted.length - 1]
  return ids
}

// Tree Items utils
const entityToTreeItem = ({
  entity,
  defaultChecked,
  defaultCheckedIds
}: {
  entity: {
    id: string
    entityType: EntityType
    name: string
  }
  defaultChecked?: boolean
  defaultCheckedIds?: string[]
}): TreeItemI => {
  return {
    type: entity.entityType,
    checked: defaultCheckedIds?.includes(entity.id) ?? defaultChecked ?? false,
    id: entity.id,
    label: entity.name,
    treeItems: []
  }
}

const hotelAuxListToTreeItem = ({
  hotels,
  roomTypes,
  defaultChecked,
  defaultCheckedIds
}: {
  hotels: HotelAuxList[]
  roomTypes?: RoomTypeAuxList[]
  defaultChecked?: boolean
  defaultCheckedIds?: string[]
}): TreeItemI[] => {
  return hotels.map((hotel) => {
    const hotelRoomTypes =
      roomTypes?.filter((roomType) => roomType.hotelId === hotel.id) ?? []
    return {
      ...entityToTreeItem({
        entity: hotel,
        defaultChecked,
        defaultCheckedIds
      }),
      treeItems: hotelRoomTypes.map((hotel) =>
        entityToTreeItem({
          entity: hotel,
          defaultChecked,
          defaultCheckedIds
        })
      )
    }
  })
}

const getChildrenZones = ({
  zones,
  hotels,
  parentId,
  defaultChecked,
  defaultCheckedIds,
  roomTypes
}: {
  parentId: string
  roomTypes?: RoomTypeAuxList[]
  zones: ZoneAuxList[]
  hotels: HotelAuxList[]
  defaultChecked?: boolean
  defaultCheckedIds?: string[]
}): TreeItemI[] => {
  return zones
    .filter((zone) => zone.parentId === parentId)
    .map((zone) => ({
      ...entityToTreeItem({
        entity: zone,
        defaultChecked,
        defaultCheckedIds
      }),
      treeItems: [
        ...getChildrenZones({
          zones,
          hotels,
          defaultChecked,
          defaultCheckedIds,
          parentId: zone.id
        }),
        ...hotelAuxListToTreeItem({
          hotels: hotels.filter((hotel) => hotel.zoneId === zone.id),
          roomTypes,
          defaultChecked,
          defaultCheckedIds
        })
      ]
    }))
}

const zoneListToTreeItem = ({
  zones,
  hotels,
  roomTypes,
  defaultChecked,
  defaultCheckedIds
}: {
  zones: ZoneAuxList[]
  hotels: HotelAuxList[]
  roomTypes?: RoomTypeAuxList[]
  defaultChecked?: boolean
  defaultCheckedIds?: string[]
}): TreeItemI[] => {
  const parentZones = zones.filter((zone) => !zone.parentId)

  return parentZones.map((parentZone) => {
    const parentZoneHotels = hotels.filter(
      (hotel) => hotel.zoneId === parentZone.id
    )
    return {
      ...entityToTreeItem({
        entity: parentZone,
        defaultChecked,
        defaultCheckedIds
      }),
      treeItems: [
        ...getChildrenZones({
          hotels,
          roomTypes,
          parentId: parentZone.id,
          zones,
          defaultChecked,
          defaultCheckedIds
        }),
        ...hotelAuxListToTreeItem({
          hotels: parentZoneHotels,
          roomTypes,
          defaultChecked,
          defaultCheckedIds
        })
      ]
    }
  })
}

const environmentListToTreeItem = ({
  environmentDataAuxList,
  zoneAuxList,
  hotelAuxList,
  roomTypes,
  defaultChecked,
  defaultCheckedIds
}: {
  environmentDataAuxList: EnvironmentDataAuxList[]
  zoneAuxList: ZoneAuxList[]
  hotelAuxList: HotelAuxList[]
  roomTypes: RoomTypeAuxList[]
  defaultChecked?: boolean
  defaultCheckedIds?: string[]
}): TreeItemI[] => {
  const treeItems = environmentDataAuxList.map((environment) => {
    const environmentZones = zoneAuxList.filter(
      (zone) => zone.environmentDataId === environment.id
    )
    const environmentHotels = hotelAuxList.filter(
      (hotel) => hotel.environmentDataId === environment.id && !hotel.zoneId
    )
    return {
      ...entityToTreeItem({
        entity: environment,
        defaultChecked,
        defaultCheckedIds
      }),
      treeItems: [
        ...zoneListToTreeItem({
          zones: environmentZones,
          hotels: hotelAuxList,
          roomTypes,
          defaultChecked,
          defaultCheckedIds
        }),
        ...hotelAuxListToTreeItem({
          hotels: environmentHotels,
          roomTypes,
          defaultChecked,
          defaultCheckedIds
        })
      ]
    }
  })
  return treeItems
}

export const hotelDataUtils = {
  hotelDataListToRatesRowData,
  environmentListToTreeItem,
  hotelAuxListToTreeItem,
  zoneListToTreeItem,
  getRowDataIds,
  channelsToRowData,
  capacityDataToRatesRowData,
  capacityDataListToRatesRowData
}
